<template>
  <div class="chirashi" :style="styleObject" :class="displayModal">
    <sp-page-header
      :page-name="spHeaderName"
      :active-my-store-id="myStoreStore.activeMyStoreId"
      :utm-source="myStoreStore.utmSource"
      @toggle-modal="toggleModal"
    ></sp-page-header>
    <div class="chirashi__section--my-tenpo-pc">
      <div
        v-if="isFlyerView && activeShopName && !isSingleStore"
        class="chirashi__viewer-store-name"
      >
        {{ activeShopName }}
      </div>
    </div>
    <div
      class="chirashi__container"
      :class="`${noContent.type}-container`"
    >
      <div class="chirashi__dropdown-container">
        <NoContent
          v-if="noContent.flag"
          text="チラシ"
          class="chirashi__no-content"
        />
        <template v-if="chirashiStore.flyers.length">
          <chirashi-swiper
            :items="chirashiStore.flyers"
            type="list"
            :store-id="myStoreStore.activeMyStoreId"
            :utm-source="myStoreStore.utmSource"
            :initial-slide="chirashiStore.activeSlideIndex"
            activate-logging
            :chirashi-view="isFlyerView"
            :location="getChirashiLocation()"
            @intersect="handleFlyerIndexCardIntersect"
            @change="changeSlide($event)"
            @click-chirashi="onClickChirashi"
          />
        </template>
      </div>
      <template v-if="0 in chirashiStore.recommendsItems">
        <h2 class="chirashi__header">メーカーからのおすすめ</h2>
        <div class="chirashi__maker-recommended-container">
          <maker-recommended-list
            :items="chirashiStore.recommendsItems"
            :location="getMakerRecommendLocation()"
            @intersect="handleMakerRecommendIntersect"
          />
        </div>
      </template>
      <template v-if="0 in chirashiStore.themes">
        <div
          v-for="(theme, index) in chirashiStore.themes"
          :key="index"
          class="chirashi__section"
        >
          <div class="chirashi__white-background">
            <related-products
              v-bind="theme"
              :side-padding="false"
              activate-logging
              activate-product-logging
              :theme-location="getThemeLocation(index)"
              :product-location="getProductLocation(index)"
              :utm-source="myStoreStore.utmSource"
              @intersect="handleRelatedProductIntersect"
              @intersect-product="handleProductIntersect"
            />
            <div class="chirashi__more-btn-container">
              <arrow-button
                :link="theme.link"
                button-color="light"
                text="もっと見る"
                class="chirashi__more-btn"
                @click.native="onClickThemeMore(index)"
              />
            </div>
          </div>
          <editors-recommended
            v-if="0 in chirashiStore.sliceTablevas(index)"
            class="chirashi__editors-recommended"
            :items="chirashiStore.sliceTablevas(index)"
            :utm-source="myStoreStore.utmSource"
            activate-logging
            :location="getTablevaLocation(index)"
            @intersect="handleTablevaIntersect($event)"
          />
        </div>
      </template>
      <template v-if="isFlyerView && !(0 in chirashiStore.themes)">
        <div v-if="chirashiStore.tablevas.length > 0" class="chirashi__viewer-recommended">
          <editors-recommended
            class="chirashi__viewer-recommended-item"
            :items="chirashiStore.tablevas"
            :utm-source="myStoreStore.utmSource"
            activate-logging
            :location="getTablevaLocation(0)"
            @intersect="handleTablevaIntersect($event)"
          />
        </div>
      </template>
      <loading v-if="!chirashiStore.isFlyerLoaded" scope="single" :loaded="chirashiStore.isLoaded" />
    </div>
  </div>
</template>

<script setup>
import ChirashiSwiper from '@/components/ChirashiSwiper'
import RelatedProducts from '@/components/RelatedProducts'
import EditorsRecommended from '@/components/EditorsRecommended'
import MakerRecommendedList from '@/components/MakerRecommendedList'
import SpPageHeader from '@/components/SpPageHeader'

import { useRoute, useRouter } from 'vue-router'
// import { apiError } from '@/plugins/error'

import { useStore } from '@/stores/index'
import { useChirashiStore } from '@/stores/chirashi'
import { useMyStoreStore } from '@/stores/myStore'
import { useImpressionStore } from '@/stores/impression'
import { useContentStore } from '@/stores/content'
// import { receiveMessageOnPort } from 'worker_threads'

const { $isSp } = useNuxtApp()

const rootStore = useStore()
const chirashiStore = useChirashiStore()
const myStoreStore = useMyStoreStore()
const impressionStore = useImpressionStore()
const contentStore = useContentStore()
const route = useRoute()
const router = useRouter()
const runtimeConfig = useRuntimeConfig()

const meta = ref({
  url: `${runtimeConfig.public.chirashiUrl}/${runtimeConfig.public.chainKey}/${runtimeConfig.public.paddingedChainId}${route.fullPath}`,
})
const shopId = ref(null)
const modal = ref(false)

const noContent = computed(() => {
  if (
    !chirashiStore.flyers.length &&
    !(0 in chirashiStore.recommendsItems) &&
    !(0 in chirashiStore.themes) &&
    !(0 in chirashiStore.sliceTablevas(0)) &&
    rootStore.isContentLoaded
  ) {
    return {
      flag: true,
      type: 'no-content',
    }
  }
  if (isFlyerView.value && !chirashiStore.flyers.length && rootStore.isContentLoaded) {
    return {
      flag: true,
      type: 'no-flyer',
    }
  }
  return {
    flag: false,
    type: 'is-content',
  }
})

const isFlyerView = computed(() => {
  return true
})

const spHeaderName = computed(() => {
  if (isFlyerView.value) {
    return activeShopName.value
  }
  return 'チラシ'
})

const pageTitle = computed(() => {
  return !rootStore.isContentLoaded ? '' : isSingleStore.value ? `${runtimeConfig.public.chainName}のチラシ | ${runtimeConfig.public.chainName}` : `${activeShopName.value}のチラシ | ${runtimeConfig.public.chainName}`
})

const pageDescription = computed(() => {
  return isSingleStore.value
    ? `${runtimeConfig.public.chainName}のチラシ情報を掲載` 
    : `${runtimeConfig.public.chainName}${activeShopName.value}のチラシ情報を掲載`
})

const styleObject = computed(() => {
  return {
    '--no-content-sp-height':
      noContent.value.type === 'no-flyer'
        ? '16.8rem'
        : isFlyerView.value
        ? 'calc(100vh - (49px + env(safe-area-inset-bottom) + 6.4rem + 4.4rem + 2.4rem))'
        : 'calc(100vh - (49px + env(safe-area-inset-bottom) + 6.4rem + 6.4rem + 4.4rem + 2.4rem))', // 100vh - (navbar + header + footer + dropdown + padding)
    '--no-content-tablet-height':
      noContent.value.type === 'no-flyer'
        ? '16.8rem'
        : 'calc(100vh - 8.9rem - 6.2rem - 4rem - 7.2rem)',
    '--no-content-pc-height':
      noContent.value.type === 'no-flyer'
        ? '16.8rem'
        : 'calc(100vh - 8.9rem - 6.2rem - 4rem - 8.8rem)',
    '--no-content-sp-margin':
      noContent.value.type === 'no-flyer'
        ? '0.8rem 1.6rem 1.6rem'
        : '1.6rem 1.6rem 0.8rem',
    '--no-content-tablet-margin':
      noContent.value.type === 'no-flyer' ? '1.6rem 0 0' : '0 0 5.6rem',
    '--no-content-pc-margin':
      noContent.value.type === 'no-flyer' ? '1.6rem 0 0' : '0 0 7.2rem',
    '--no-content-sp-border-width':
      noContent.value.type === 'no-content' || chirashiContentOnly.value
        ? '0'
        : '0.05rem',
  }
})

const activeShopName = computed(() => {
  const activeShop = GETSHOP(myStoreStore.activeMyStoreId, myStoreStore.stores)
  return activeShop?.shopName
})

const displayModal = computed(() => {
  return {
    modal: modal.value,
  }
})

const chirashiContentOnly = computed(() => {
  return (
    noContent.value.type === 'is-content' &&
    !(0 in chirashiStore.recommendsItems) &&
    !(0 in chirashiStore.themes)
  )
})

const isSingleStore = computed(() => {
  return runtimeConfig.public.singleStore
})

const init = async () => {
  try {
    rootStore.fetchChain()
    rootStore.fetchMedia()
    let activeShop
    
    await myStoreStore.fetchStores()
    activeShop = GETSHOP(myStoreStore.activeMyStoreId, myStoreStore.stores)
    if (activeShop === undefined) {
      if (isSingleStore.value) {
        throw showError({ data: {}, statusCode: 404, fatal: true })
      } else {
        router.push('/my-store/search')
      }
    }
    
    if (myStoreStore.activeMyStoreId) {
      await chirashiStore.fetchItems({
        shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
        detailId: chirashiStore.activeDetailId,
      })
    }
    if (chirashiStore.activeChirashiId) {
      await chirashiStore.fetchItem({
        shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
        id: chirashiStore.activeChirashiId,
        detailId: chirashiStore.activeDetailId,
      })
    }

    chirashiStore.fetchRecommends({
      shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
    })

    await chirashiStore.fetchTablevaContents({ page: chirashiStore.page })
  } catch (err) {
      throw showError({ data: err.data, statusCode: err.statusCode, fatal: true })
  } finally {
    rootStore.$patch({ isContentLoaded: true })
    if (route.name === 'chirashi') setMetaData()
  }
}

const changeInit = async (isAll = false) => {
  chirashiStore.$patch({ isLoaded: false})

  try {
    let activeShop
    activeShop = GETSHOP(myStoreStore.activeMyStoreId, myStoreStore.stores)

    if (isAll) {
      await chirashiStore.fetchItems({
        shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
        detailId: chirashiStore.activeDetailId,
      })
    }
    if (chirashiStore.nextChirashiId) {
      await chirashiStore.fetchItem({
        shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
        id: chirashiStore.nextChirashiId,
        detailId: chirashiStore.activeDetailId,
        keepAdvertisements: true,
      })
    }
    chirashiStore.fetchRecommends({
      shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
    })


    const interval = setInterval(async () => {
      if (chirashiStore.isTablevasLoaded) {
        if (chirashiStore.activeChirashiId !== chirashiStore.nextChirashiId)
          chirashiStore.$patch({ activeChirashiId: chirashiStore.nextChirashiId })
        await chirashiStore.fetchTablevaContents({ page: chirashiStore.page })
        clearInterval(interval)
      }
    }, 500)
  } catch (err) {
      console.log(err)
      throw showError({ data: err.data, statusCode: err.statusCode, fatal: true })
  } finally {
      chirashiStore.$patch({ isLoaded: true })
  }
}

const changeStore = async (newShopId) => {
  myStoreStore.$patch({ activeMyStoreId: newShopId })
  chirashiStore.initFlyer()
  await changeInit(true)
}


const changeSlide = async (index) => {
  if (chirashiStore.flyers[index]?.id && rootStore.isContentLoaded) {
    chirashiStore.$patch({ nextChirashiId: chirashiStore.flyers[index]?.id})
    chirashiStore.$patch({ activeDetailId: chirashiStore.flyers[index]?.detailId })
    chirashiStore.initTheme()
    await changeInit()
  }
}

const onResize = () => {
  if ($isSp(window.innerWidth)) {
    chirashiStore.$patch({ displayTablevaItems: 6 })
  } else {
    chirashiStore.$patch({ displayTablevaItems: 6 })
  }
}

const handleFlyerIndexCardIntersect = ({ id, detailId, location }) => {
  let activeShop
  activeShop = GETSHOP(myStoreStore.activeMyStoreId, myStoreStore.stores)
  
  impressionStore.sendFlyerListImpression({
    id,
    detailId,
    shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
    location,
  })
}

const handleMakerRecommendIntersect = ({ id, location }) => {
  let activeShop
  activeShop = GETSHOP(myStoreStore.activeMyStoreId, myStoreStore.stores)

  impressionStore.sendMakerListImpression({
    id,
    shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
    flyerlId: chirashiStore.activeChirashiId,
    location,
  })
}

const handleRelatedProductIntersect = ({ id, location }) => {
  let activeShop
    activeShop = GETSHOP(myStoreStore.activeMyStoreId, myStoreStore.stores)

  impressionStore.sendThemeListImpression({
    id,
    shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
    flyerlId: chirashiStore.activeChirashiId,
    location,
  })
}

const handleProductIntersect = ({ id, groupId, themeIndex, productIndex }) => {
  const newLocation = {
    display_name: 'chirashi',
    area_name: 'theme-list',
    area_line_number: themeIndex + 1,
    element_name: 'product-list',
    element_line_number: productIndex + 1,
  }
  let activeShop
  activeShop = GETSHOP(myStoreStore.activeMyStoreId, myStoreStore.stores)

  impressionStore.sendProductListViewedImpression({
    id,
    shopId: activeShop?.shopId ?? myStoreStore.activeMyStoreId,
    flyerlId: chirashiStore.activeChirashiId,
    detailId: chirashiStore.activeDetailId,
    flyerThemeId: chirashiStore.themes[themeIndex]?.id ?? null,
    refId: groupId,
    location: newLocation,
  })
}

const handleTablevaIntersect = ({ id, themeIndex, tablevatIndex }) => {
  const newLocation = {
    display_name: 'chirashi',
    area_name: 'theme-list',
    area_line_number: themeIndex + 1,
    element_name: 'tableva-recommend-list',
    element_line_number: tablevatIndex + 1,
  }

  impressionStore.sendContentListImpression({
    id,
    location: newLocation,
  })
}

const onClickChirashi = (data) => {
  const newLocation = {
    display_name: 'chirashi',
    area_name: 'chirashi-list',
    area_line_number: 1,
    element_name: 'chirashi-list',
    element_line_number: data.index + 1,
  }
  impressionStore.setLocation(newLocation)
}

const getChirashiLocation = () => {
  const newLocation = {
    display_name: 'chirashi',
    area_name: 'chirashi-list',
    area_line_number: 1,  
    element_name: 'chirashi-list',
    element_line_number: 0,
  }
  return newLocation
}

const getMakerRecommendLocation = () => {
  const newLocation = {
    display_name: 'chirashi',
    area_name: 'advertisement-list',
    area_line_number: 1,
    element_name: 'advertisement-list',
    element_line_number: 0,
  }
  return newLocation
}

const getThemeLocation = (index) => {
  const newLocation = {
    display_name: 'chirashi',
    area_name: 'theme-list',
    area_line_number: index + 1,
    element_name: 'theme-banner',
    element_line_number: 1,
  }
  return newLocation
}

const getProductLocation = (index) => {
  const newLocation = {
    display_name: 'chirashi',
    area_name: 'theme-list',
    area_line_number: index + 1,
    element_name: 'product-list',
    element_line_number: 0,
  }
  return newLocation
}

const getTablevaLocation = (index) => {
  const newLocation = {
    display_name: 'chirashi',
    area_name: 'theme-list',
    area_line_number: index + 1,
    element_name: 'tableva-recommend-list',
    element_line_number: 0,
  }
  return newLocation
}

const onClickThemeMore = (index) => {
  const location = {
    display_name: 'chirashi',
    area_name: 'theme-list',
    area_line_number: index + 1,
    element_name: 'theme-more-button',
    element_line_number: 1,
  }
  impressionStore.setLocation(location)
}

const toggleModal = () => {
  modal.value = !modal.value
}

const setMetaData = () => {
  useHead({
    title: pageTitle.value,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: pageDescription.value,
      },
      {
        hid: 'og:title',
        property: 'og:title',
        content: pageTitle.value,
      },
      {
        hid: 'og:description',
        property: 'og:description',
        content: pageDescription.value,
      },
      {
        hid: 'og:type',
        property: 'og:type',
        content: 'website',
      },
      {
        hid: 'og:url',
        property: 'og:url',
        content: meta.value.url,
      },
      {
        hid: 'twitter:card',
        property: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        hid: 'twitter:title',
        property: 'twitter:title',
        content: pageTitle.value,
      },
      {
        hid: 'twitter:description',
        property: 'twitter:description',
        content: pageDescription.value,
      },
      {
        hid: 'twitter:url',
        property: 'twitter:url',
        content: meta.value.url,
      },
    ],
  })
}

/**
 * チラシビュアー用処理
 */
 if (route?.query['shop-id']) {
  myStoreStore.$patch({ activeMyStoreId: route.query['shop-id'] })
  localStorage.setItem(`${runtimeConfig.public.chainKey}-active-mystore-id`, route.query['shop-id'])
  if (route?.query['detail-id']) {
    chirashiStore.$patch({ activeDetailId: Number(route.query['detail-id'])})
  }
} else if (!myStoreStore.activeMyStoreId) {
  router.push({ name: 'mystore-search' })
}

onMounted(() => {
  if (chirashiStore.isBlank) {
    chirashiStore.initIframe()
  } else if (!rootStore.isHistoryBack) {
    chirashiStore.clearAll()
  }

  // contentStore.$patch({ itemDetails: [] })
  contentStore.$patch({ isRouteUpdate: false })
  rootStore.$patch({ isContentLoaded: false })  //毎回ローディングを走らせる

  if (route?.query.utm_source) {
    myStoreStore.$patch({ utmSource: route?.query.utm_source })
  } else {
    myStoreStore.$patch({ utmSource: null })
  }

  onResize()
  window.addEventListener('resize', onResize)
  if (rootStore.isHistoryBack && noContent.value.flag) {
    rootStore.$patch({ isHistoryBack: false })
    nextTick(() => {
      rootStore.$patch({ isContentLoaded: true })
      console.log('content loaded')
    })
  } else {
    init()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})


</script>

<style lang="scss" scoped>
.chirashi {
  box-sizing: border-box;
  @media screen and (min-width: $tablet-breakpoint) {
    padding: 0 2.4rem;
    max-width: 76.8rem;
    margin: auto;
  }
  @media screen and (min-width: $tablet2-breakpoint) {
    max-width: 102.4rem;
  }
  @media screen and (min-width: $pc-breakpoint) {
    padding: 0 4rem;
    max-width: 136.7rem;
  }

  .chirashi__container {
    position: relative;
    background-color: $color-bg;
    min-height: calc(100vh - (83px + 6.4rem + 4.4rem));
    @media screen and (min-width: $tablet-breakpoint) {
      padding-top: 1.6rem;
      background-color: transparent;
      min-height: auto;
    }
    .loading.scope-single {
      position: relative;
      padding: 1.6rem 0;
    }
    &.no-flyer-container {
      padding-top: 0;
    }
  }
  .chirashi__no-content {
    @media screen and (min-width: $tablet-breakpoint) {
      margin-bottom: 1rem !important;
    }
  }
  .chirashi__dropdown-container {
    background: $color-white;
    border-bottom: var(--no-content-sp-border-width) solid $color-line;
    @media screen and (min-width: $tablet-breakpoint) {
      border: none;
      background: none;
      overflow-y: auto;
      & + h2 {
        margin-top: 4.6rem;
      }
      & + div {
        margin-top: 4.6rem;
      }
    }
    @media screen and (min-width: $tablet2-breakpoint) {
      & + h2 {
        margin-top: 6.2rem;
      }
      & + div {
        margin-top: 6.2rem;
      }
    }
  }
  .chirashi__dropdown {
    width: 100%;
    @media screen and (min-width: $tablet-breakpoint) {
      padding-top: 1.6rem;
    }
  }
  .chirashi__viewer-store-name {
    font-size: 1.6rem;
    line-height: calc(24 / 16);
    color: $color-main-text;
    font-weight: bold;
    padding-top: 1.6rem;
  }
  .chirashi__header {
    color: $color-shadow;
    font-weight: 700;
    font-size: 1.6rem;
    line-height: calc(21 / 16);
    margin: 2.4rem 1.6rem 0.8rem;
    @media screen and (min-width: $tablet-breakpoint) {
      margin: 5.6rem 0 1.6rem;
      font-size: 2rem;
      line-height: calc(28.96 / 20);
    }
    @media screen and (min-width: $pc-breakpoint) {
      margin-bottom: 0.8rem;
    }
  }
  .chirashi__maker-recommended-container {
    border-top: 0.05rem solid $color-line;
    border-bottom: 0.05rem solid $color-line;
    padding: 0.8rem 0 0.8rem 1.6rem;
    background-color: $color-white;
    margin-bottom: 1.6rem;
    @media screen and (min-width: $tablet-breakpoint) {
      background-color: transparent;
      border: none;
      padding: 0;
      margin: 0;
    }
  }
  .chirashi__section--my-tenpo {
    padding: 1.6rem 1.6rem 0;
    @media screen and (min-width: $tablet-breakpoint) {
      display: none;
    }
  }

  .chirashi__section--my-tenpo-pc {
    display: none;
    @media screen and (min-width: $tablet-breakpoint) {
      display: block;
    }
  }

  .chirashi__section {
    @media screen and (min-width: $tablet-breakpoint) {
      display: flex;
      flex-direction: column;
      gap: 5.6rem;
      margin-top: 5.6rem;
    }
    @media screen and (min-width: $tablet2-breakpoint) {
      flex-direction: row;
      gap: 2.4rem;
      margin-top: 7.2rem;
    }
  }

  .chirashi__section:last-child {
    @media screen and (min-width: $tablet-breakpoint) {
      padding-bottom: 5.6rem;
    }
    @media screen and (min-width: $tablet2-breakpoint) {
      padding-bottom: 7.2rem;
    }
  }

  .chirashi__viewer-recommended {
    padding: 2.4rem 0 1.4rem 0;
    border-top: solid $color-line 0.05rem;
    border-bottom: solid $color-line 0.05rem;
    @media screen and (min-width: $tablet-breakpoint) {
      margin: 5.6rem 0;
      padding: 0;
      border: none;
    }
    @media screen and (min-width: $tablet2-breakpoint) {
      margin: 7.2rem 0;
    }
  }

  .chirashi__viewer-recommended-item {
    @media screen and (min-width: $tablet2-breakpoint) {
      ::v-deep .editors-recommended__container {
        flex-direction: row;
      }
    }
  }

  .chirashi__unregistered-container {
    height: calc(
      100vh - 4.4rem - (49px + env(safe-area-inset-bottom) + 6.4rem)
    );
    display: flex;
    flex-direction: column;
    justify-content: center;
    @media screen and (min-width: $tablet-breakpoint) {
      height: calc(100vh - 8.9rem - 6.2rem);
    }
  }

  .chirashi__white-background {
    background: $color-white;
    border-top: 0.05rem solid $color-line;
    padding-top: 2.4rem;
    @media screen and (min-width: $tablet-breakpoint) {
      background: transparent;
      padding-top: 0;
      border: none;
    }
    @media screen and (min-width: $tablet2-breakpoint) {
      ::v-deep .related-products {
        width: 60.8rem;
      }
    }
    @media screen and (min-width: $pc-breakpoint) {
      ::v-deep .related-products {
        width: 80rem;
      }
    }
  }
  // .chirashi__white-background:first-child {
  //   border-top: none;
  // }
  // .chirashi__white-background:last-child {
  //   border-bottom: none;
  // }

  .chirashi__more-btn-container {
    padding: 1.6rem 1.6rem 2.4rem;
    @media screen and (min-width: $tablet-breakpoint) {
      padding: 2.4rem 0 2.4rem 0;
      display: flex;
      justify-content: center;
    }
  }

  .chirashi__more-btn {
    @media screen and (min-width: $tablet-breakpoint) {
      width: 30rem;
    }
  }

  .chirashi__editors-recommended {
    padding: 2.4rem 0;
    border-top: $color-line solid 0.05rem;
    border-bottom: $color-line solid 0.05rem;
    @media screen and (min-width: $tablet-breakpoint) {
      background: none;
      padding: 0;
      width: 100%;
      border: none;
    }
  }

  .my-store-registration-splash {
    margin: 0 1.6rem;
    @media screen and (min-width: $tablet-breakpoint) {
      margin: 0 auto;
    }
  }

  .no-content {
    margin: var(--no-content-sp-margin);
    height: var(--no-content-sp-height);
    @media screen and (min-width: $tablet-breakpoint) {
      height: var(--no-content-tablet-height);
      margin: var(--no-content-tablet-margin);
    }
    @media screen and (min-width: $tablet2-breakpoint) {
      height: var(--no-content-pc-height);
      margin: var(--no-content-pc-margin);
    }
  }
}
.modal {
  width: 100%;
  position: fixed;
  z-index: 98;
}
</style>
